import { FC, useEffect, useRef } from 'react';

import cs from 'classnames';
import Plyr from 'plyr-react';
import 'plyr-react/plyr.css';

import Section, { SectionBackgroundPictureName } from 'layouts/Section/Section';

import SectionTitle, {
  SectionTitleColor
} from 'components/SectionTitle/SectionTitle';

import styles from './MediaSection.module.scss';

type MediaSectionColorScheme = 'light' | 'dark';

const COLOR_SCHEMES = {
  light: {
    background: '--white',
    titleColor: 'blue',
    backgroundPictureName: ''
  },
  dark: {
    background: '--brand-secondary-darken',
    titleColor: 'white-yellow',
    backgroundPictureName: 'wave'
  }
};

interface MediaSectionProps {
  videoLink: string;
  mainText: string;
  highlightText?: string;
  colorScheme?: MediaSectionColorScheme;
  children?: React.ReactNode;
  // solution to get rid of black column on the sides of the video
  isScaleVideo?: boolean;
}

const MediaSection: FC<MediaSectionProps> = ({
  videoLink,
  mainText,
  highlightText,
  colorScheme = 'dark',
  isScaleVideo = false,
  children
}: MediaSectionProps) => {
  const videoRef = useRef(null);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!videoRef.current) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && videoRef.current) {
          (videoRef.current as any).plyr?.play();
        }
      },
      { threshold: 0.5 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [videoRef.current]);

  const source: Plyr.SourceInfo = {
    type: 'video',
    sources: [
      {
        src: videoLink,
        provider: 'vimeo'
      }
    ]
  };

  const options: Plyr.Options = {
    muted: true,
    controls: ['play', 'mute', 'progress'],
    disableContextMenu: true,
    vimeo: {
      title: false
    }
  };

  const scheme = COLOR_SCHEMES[colorScheme];

  return (
    <Section
      className={styles.wrap}
      background={scheme.background}
      backgroundPictureName={
        scheme.backgroundPictureName as SectionBackgroundPictureName
      }
    >
      <article className={styles.head} ref={sectionRef}>
        <SectionTitle
          highlightText={highlightText}
          mainText={mainText}
          color={scheme.titleColor as SectionTitleColor}
          textAlign="center"
          className={styles.title}
        />

        <div className={styles.content}>
          <div
            className={cs(styles.text, {
              [styles.dark]: colorScheme === 'light',
              [styles.light]: colorScheme === 'dark'
            })}
          >
            {children}
          </div>

          <div
            className={cs(styles.videoContainer, {
              [styles.scale]: isScaleVideo
            })}
          >
            <Plyr ref={videoRef} source={source} options={options} />
          </div>
        </div>
      </article>
    </Section>
  );
};

export default MediaSection;
